import { getSourcesInfo } from "@constants/sources";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { linkPillStyles } from "@theme/styles";
import React from "react";
import { useTranslation } from "react-i18next";

export interface SourcesButtonProps {
  sourceIds: string[];
  onClick?: () => void;
}

const SourcesButton: React.FC<SourcesButtonProps> = ({
  sourceIds,
  onClick
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  if (!sourceIds || sourceIds.length === 0) {
    return null;
  }

  const sourcesInfo = getSourcesInfo(sourceIds);
  const maxIcons = 3;
  const iconsToShow = sourcesInfo.slice(0, maxIcons);
  const totalCount = sourceIds.length;

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        ...linkPillStyles(theme),
        alignSelf: "flex-start",
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: "2px" // Padding izquierdo específico para los iconos
      }}
    >
      {/* Icons container - max 3 overlapping icons */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          height: "100%",
          width:
            iconsToShow.length > 0
              ? `${16 + (iconsToShow.length - 1) * 8}px`
              : 0
        }}
      >
        {iconsToShow.map((source, index) => {
          const domain = source.url.replace(/^https?:\/\//, "");
          const SourceIcon = () => {
            const [imgError, setImgError] = React.useState(false);

            return (
              <Box
                sx={{
                  position: "absolute",
                  left: `${index * 8}px`,
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: `2px solid ${theme.palette.customColors.chipBg}`,
                  bgcolor: theme.palette.customColors.chipBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: maxIcons - index,
                  overflow: "hidden"
                }}
              >
                {!imgError ? (
                  <Box
                    component="img"
                    src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
                    alt=""
                    onError={() => setImgError(true)}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      bgcolor: theme.palette.primary.main
                    }}
                  />
                )}
              </Box>
            );
          };
          return <SourceIcon key={index} />;
        })}
      </Box>

      {/* Count text */}
      <Typography
        sx={{
          fontFamily: "Ringside Narrow",
          fontSize: "14px",
          fontWeight: 325,
          fontStyle: "normal",
          lineHeight: "14px",
          letterSpacing: "0%",
          color: "#888888",
          display: "inline"
        }}
      >
        {t("sources.count", {
          count: totalCount,
          defaultValue: totalCount === 1 ? "fuente" : "fuentes"
        })}
      </Typography>
    </Box>
  );
};

export default SourcesButton;
